import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, Loader2, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when the user lands with a recovery token
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) setTimedOut(true);
    }, 6000);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        resolved = true;
        clearTimeout(timeout);
        setIsReady(true);
      }
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setIsLoading(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setError(error.message);
    } else {
      setIsDone(true);
      setTimeout(() => navigate("/login"), 2500);
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center relative overflow-hidden">
      {/* Dot grid background */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:24px_24px]" />

      {/* Indigo glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-indigo-500/10 blur-[100px] rounded-full" />

      <div className="relative z-10 w-full max-w-sm mx-auto px-4">
        {/* Logo mark */}
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
          <Zap className="w-5 h-5 text-white" />
        </div>

        <p className="text-2xl font-bold font-display bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent text-center">
          DiscoRun
        </p>

        <p className="text-sm text-white/40 text-center mt-1 mb-8">Payment Term Control Center</p>

        <div
          className="rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl p-8"
          style={{
            boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          {isDone ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/25 flex items-center justify-center mx-auto">
                <svg
                  className="w-6 h-6 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white/80 mb-1">Password updated</p>
                <p className="text-xs text-white/40">Redirecting you to sign in…</p>
              </div>
            </div>
          ) : !isReady ? (
            <div className="text-center space-y-3 py-2">
              {timedOut ? (
                <>
                  <p className="text-sm font-semibold text-white/70">Reset link expired</p>
                  <p className="text-xs text-white/40">
                    This link is no longer valid. Please request a new one.
                  </p>
                </>
              ) : (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-400 mx-auto" />
                  <p className="text-xs text-white/40">Verifying your reset link…</p>
                </>
              )}
              <p className="text-[11px] text-white/25">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-indigo-400/70 hover:text-indigo-400 underline"
                >
                  Request a new one.
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="text-sm font-semibold text-white/80 mb-1">Set new password</h2>
                <p className="text-xs text-white/40">
                  Choose a password with at least 8 characters.
                </p>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="new-password"
                  className="block text-[11px] uppercase tracking-wider font-medium text-white/50"
                >
                  New Password
                </label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoFocus
                    className="bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 focus:border-indigo-500/60 focus:ring-0 focus:ring-offset-0 rounded-lg pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="confirm-password"
                  className="block text-[11px] uppercase tracking-wider font-medium text-white/50"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 focus:border-indigo-500/60 focus:ring-0 focus:ring-offset-0 rounded-lg pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    tabIndex={-1}
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-400 hover:to-cyan-400 text-white font-medium transition-all duration-200 hover:shadow-[0_0_20px_rgba(99,102,241,0.35)] border-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating…
                  </>
                ) : (
                  "Update Password"
                )}
              </Button>
            </form>
          )}
        </div>

        <p className="text-white/20 text-xs text-center mt-6">
          © 2026 DiscoRun. All rights reserved.
        </p>
      </div>
    </div>
  );
}
